import { useState } from 'react';
import { ChevronRight, LogOut } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useAuth } from '../lib/AuthContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import Header from '../components/Header';
import AboutCivicbeatModal from '../components/AboutCivicbeatModal';
import ContactCivicbeatModal from '../components/ContactCivicbeatModal';
import PrivacyDataModal from '../components/PrivacyDataModal';

export default function Settings() {
  const { navigateTo, showToast } = useApp();
  const { signOut } = useAuth();
  const { user, isPreviewMode, isLoggedOut } = useEffectiveUser();
  const [showAbout, setShowAbout] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const handleSignOut = async () => {
    if (isPreviewMode) {
      showToast('Preview mode: Sign out disabled');
      return;
    }
    await signOut();
    showToast('Signed out successfully');
    navigateTo('home');
  };

  return (
    <div className="min-h-screen bg-[#111111] pb-24 md:pb-8">
      <Header
        mode="title"
        title="Settings"
        showBackButton
        onBackClick={() => navigateTo('profile')}
      />

      <div className="px-6 py-8">
        <div className="max-w-[520px] mx-auto space-y-12">
        {/* ===== ACCOUNT ===== */}
        <div>
          <div className="text-[11px] font-semibold text-[#606060] mb-5 tracking-[0.08em] uppercase">
            Account
          </div>
          <div className="bg-[#181818] rounded-xl border border-[#282828] overflow-hidden" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            {user ? (
              <>
                <button
                  className="w-full px-4 py-4 flex items-center justify-between border-b border-[#111111] transition-colors hover:bg-[#1A1A1A]"
                  onClick={() => navigateTo('profile-settings')}
                >
                  <span className="text-[15px] text-[#F5F5F5]">Profile & Account Settings</span>
                  <ChevronRight className="w-5 h-5 text-[#A0A0A0]" />
                </button>
                <button
                  className="w-full px-4 py-4 flex items-center justify-between transition-colors hover:bg-[#1A1A1A]"
                  onClick={handleSignOut}
                >
                  <div className="flex items-center gap-3">
                    <LogOut className="w-4 h-4 text-[#FF6F61]" />
                    <span className="text-[15px] text-[#FF6F61]">Sign Out</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#FF6F61]/60" />
                </button>
              </>
            ) : (
              <>
                <button
                  className="w-full px-4 py-4 flex items-center justify-between border-b border-[#111111] transition-colors hover:bg-[#1A1A1A]"
                  onClick={() => navigateTo('auth', undefined, { authMode: 'signup' })}
                >
                  <div className="text-left">
                    <div className="text-[15px] text-[#40E0D0] font-medium">Create Account</div>
                    <div className="text-[12px] text-[#808080] mt-0.5 leading-tight">
                      Save your stories and progress across devices.
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#40E0D0]" />
                </button>
                <button
                  className="w-full px-4 py-4 flex items-center justify-between transition-colors hover:bg-[#1A1A1A]"
                  onClick={() => navigateTo('auth', undefined, { authMode: 'signin' })}
                >
                  <span className="text-[15px] text-[#F5F5F5]">Sign In</span>
                  <ChevronRight className="w-5 h-5 text-[#A0A0A0]" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* ===== ABOUT ===== */}
        <div>
          <div className="text-[11px] font-semibold text-[#606060] mb-5 tracking-[0.08em] uppercase">
            About
          </div>
          <div className="bg-[#181818] rounded-xl border border-[#282828] overflow-hidden" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <button
              className="w-full px-4 py-4 flex items-center justify-between border-b border-[#111111] transition-colors hover:bg-[#1A1A1A]"
              onClick={() => setShowAbout(true)}
            >
              <span className="text-[15px] text-[#F5F5F5]">About</span>
              <ChevronRight className="w-5 h-5 text-[#A0A0A0]" />
            </button>
            <button
              className="w-full px-4 py-4 flex items-center justify-between border-b border-[#111111] transition-colors hover:bg-[#1A1A1A]"
              onClick={() => setShowContact(true)}
            >
              <span className="text-[15px] text-[#F5F5F5]">Contact</span>
              <ChevronRight className="w-5 h-5 text-[#A0A0A0]" />
            </button>
            <button
              className="w-full px-4 py-4 flex items-center justify-between transition-colors hover:bg-[#1A1A1A]"
              onClick={() => setShowPrivacy(true)}
            >
              <span className="text-[15px] text-[#F5F5F5]">Privacy Policy</span>
              <ChevronRight className="w-5 h-5 text-[#A0A0A0]" />
            </button>
          </div>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="pt-6 text-center">
          <div className="text-[11px] text-[#505050]">
            civicbeat v0.1
          </div>
        </div>
        </div>
      </div>

      {/* ===== ABOUT MODAL ===== */}
      <AboutCivicbeatModal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
      />

      {/* ===== CONTACT MODAL ===== */}
      <ContactCivicbeatModal
        isOpen={showContact}
        onClose={() => setShowContact(false)}
      />

      {/* ===== PRIVACY & DATA MODAL ===== */}
      <PrivacyDataModal
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />
    </div>
  );
}
